import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ShieldAlert, Download, RefreshCw, ArrowLeft } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';

const SEVERITY_STYLES = {
  Critical: 'bg-red-600 text-white',
  Major: 'bg-orange-500 text-white',
  Minor: 'bg-yellow-500 text-black',
};

const SEVERITIES = ['All', 'Critical', 'Major', 'Minor'];
const KINDS = ['All', 'audit', 'incident'];

const AdminPolicyViolations = ({ user, setUser }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState(null);
  const [violations, setViolations] = useState([]);
  const [severityFilter, setSeverityFilter] = useState('All');
  const [kindFilter, setKindFilter] = useState('All');
  const [showDismissed, setShowDismissed] = useState(false);
  const [search, setSearch] = useState('');
  const isEs = i18n.language === 'es';

  const labels = isEs
    ? {
        title: 'Tendencias de Violaciones de Política',
        subtitle: 'Detectadas por IA al cruzar auditorías e incidentes con manuales y políticas',
        back: 'Volver al Panel',
        refresh: 'Actualizar',
        exportCsv: 'Exportar CSV',
        totalOpen: 'Abiertas',
        critical: 'Críticas',
        major: 'Mayores',
        minor: 'Menores',
        bySeverity: 'Por Severidad',
        byKind: 'Por Fuente',
        topSources: 'Políticas Más Violadas',
        recentViolations: 'Violaciones Recientes',
        searchPlaceholder: 'Buscar fuente o descripción…',
        severity: 'Severidad',
        confidence: 'Confianza',
        source: 'Fuente',
        kind: 'Tipo',
        record: 'Registro',
        description: 'Problema',
        action: 'Acción Sugerida',
        status: 'Estado',
        dismissed: 'Descartada',
        open: 'Abierta',
        none: 'No se han detectado violaciones todavía. Ejecute análisis de IA en sus auditorías e incidentes.',
        showDismissed: 'Ver descartadas',
        hideDismissed: 'Ocultar descartadas',
        count: 'Cantidad',
        audit: 'Auditoría',
        incident: 'Incidente',
        date: 'Fecha',
      }
    : {
        title: 'Policy Violation Trends',
        subtitle: 'AI-detected by cross-referencing audits & incidents against manuals and policies',
        back: 'Back to Dashboard',
        refresh: 'Refresh',
        exportCsv: 'Export CSV',
        totalOpen: 'Open',
        critical: 'Critical',
        major: 'Major',
        minor: 'Minor',
        bySeverity: 'By Severity',
        byKind: 'By Source',
        topSources: 'Most Violated Policies',
        recentViolations: 'Recent Violations',
        searchPlaceholder: 'Search source or description…',
        severity: 'Severity',
        confidence: 'Confidence',
        source: 'Source',
        kind: 'Kind',
        record: 'Record',
        description: 'Issue',
        action: 'Suggested Action',
        status: 'Status',
        dismissed: 'Dismissed',
        open: 'Open',
        none: 'No policy violations detected yet. Run AI analysis on your audits and incidents.',
        showDismissed: 'Show dismissed',
        hideDismissed: 'Hide dismissed',
        count: 'Count',
        audit: 'Audit',
        incident: 'Incident',
        date: 'Date',
      };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (severityFilter !== 'All') params.set('severity', severityFilter);
        if (kindFilter !== 'All') params.set('record_kind', kindFilter);
        params.set('dismissed', showDismissed ? 'true' : 'false');
        params.set('limit', '500');

        const [trendsRes, listRes] = await Promise.all([
          axiosInstance.get('/policy-violations/trends'),
          axiosInstance.get(`/policy-violations?${params.toString()}`),
        ]);
        if (cancelled) return;
        setTrends(trendsRes.data);
        setViolations(listRes.data.violations || []);
      } catch (e) {
        if (!cancelled) toast.error(isEs ? 'Error al cargar tendencias' : 'Failed to load trends');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [severityFilter, kindFilter, showDismissed, isEs]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (severityFilter !== 'All') params.set('severity', severityFilter);
      if (kindFilter !== 'All') params.set('record_kind', kindFilter);
      params.set('dismissed', showDismissed ? 'true' : 'false');
      params.set('limit', '500');
      const [trendsRes, listRes] = await Promise.all([
        axiosInstance.get('/policy-violations/trends'),
        axiosInstance.get(`/policy-violations?${params.toString()}`),
      ]);
      setTrends(trendsRes.data);
      setViolations(listRes.data.violations || []);
    } catch (e) {
      toast.error(isEs ? 'Error al cargar tendencias' : 'Failed to load trends');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return violations;
    return violations.filter(
      (v) =>
        (v.source || '').toLowerCase().includes(term) ||
        (v.description || '').toLowerCase().includes(term) ||
        (v.quote || '').toLowerCase().includes(term)
    );
  }, [violations, search]);

  const exportCsv = () => {
    const headers = [
      labels.date,
      labels.severity,
      labels.confidence,
      labels.kind,
      labels.source,
      labels.description,
      labels.action,
      labels.status,
    ];
    const rows = filtered.map((v) => [
      (v.createdAt || '').slice(0, 19).replace('T', ' '),
      v.severity,
      v.confidence,
      v.recordKind,
      (v.source || '').replace(/"/g, '""'),
      (v.description || '').replace(/"/g, '""'),
      (v.correctiveAction || '').replace(/"/g, '""'),
      v.dismissed ? labels.dismissed : labels.open,
    ]);
    const csv =
      [headers, ...rows]
        .map((row) =>
          row
            .map((cell) =>
              typeof cell === 'string' && /[",\n]/.test(cell)
                ? `"${cell}"`
                : cell
            )
            .join(',')
        )
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `policy-violations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(isEs ? 'CSV exportado' : 'CSV exported');
  };

  const sev = trends?.bySeverity || { Critical: 0, Major: 0, Minor: 0 };
  const total = trends?.totalOpen || 0;
  const topSources = trends?.topSources || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav user={user} setUser={setUser} />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                data-testid="policy-trends-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {labels.back}
              </Button>
            </div>
            <h1 className="text-3xl font-bold mt-2 flex items-center gap-2">
              <ShieldAlert className="w-7 h-7 text-red-600" />
              {labels.title}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{labels.subtitle}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
              data-testid="policy-trends-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {labels.refresh}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={filtered.length === 0}
              data-testid="policy-trends-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              {labels.exportCsv}
            </Button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="policy-trends-stat-total">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {labels.totalOpen}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{total}</div>
            </CardContent>
          </Card>
          <Card className="border-red-200" data-testid="policy-trends-stat-critical">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700">
                {labels.critical}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-700">{sev.Critical || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-orange-200" data-testid="policy-trends-stat-major">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-600">
                {labels.major}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{sev.Major || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200" data-testid="policy-trends-stat-minor">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-yellow-700">
                {labels.minor}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-700">{sev.Minor || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Top Sources */}
        {topSources.length > 0 && (
          <Card className="mb-6" data-testid="policy-trends-top-sources">
            <CardHeader>
              <CardTitle className="text-lg">{labels.topSources}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {topSources.slice(0, 10).map((s, idx) => {
                  const max = topSources[0]?.count || 1;
                  const pct = Math.round((s.count / max) * 100);
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-1/3 text-sm font-medium truncate" title={s.source}>
                        {s.source}
                      </div>
                      <div className="flex-1 h-3 bg-gray-200 rounded">
                        <div
                          className="h-3 rounded bg-red-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-sm font-semibold">
                        {s.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-4 flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={labels.searchPlaceholder}
                data-testid="policy-trends-search-input"
              />
            </div>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-[160px]" data-testid="policy-trends-severity-filter">
                <SelectValue placeholder={labels.severity} />
              </SelectTrigger>
              <SelectContent>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === 'All'
                      ? isEs ? 'Todas' : 'All'
                      : s === 'Critical' ? labels.critical
                      : s === 'Major' ? labels.major
                      : labels.minor}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[160px]" data-testid="policy-trends-kind-filter">
                <SelectValue placeholder={labels.kind} />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k === 'All' ? isEs ? 'Todos' : 'All'
                      : k === 'audit' ? labels.audit
                      : labels.incident}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showDismissed ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowDismissed((v) => !v)}
              data-testid="policy-trends-toggle-dismissed"
            >
              {showDismissed ? labels.hideDismissed : labels.showDismissed}
            </Button>
          </CardContent>
        </Card>

        {/* Violations table */}
        <Card data-testid="policy-trends-list">
          <CardHeader>
            <CardTitle className="text-lg">
              {labels.recentViolations} ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-600 py-6 text-center">{labels.none}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{labels.date}</TableHead>
                      <TableHead>{labels.severity}</TableHead>
                      <TableHead>{labels.confidence}</TableHead>
                      <TableHead>{labels.kind}</TableHead>
                      <TableHead>{labels.source}</TableHead>
                      <TableHead>{labels.description}</TableHead>
                      <TableHead>{labels.status}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((v) => (
                      <TableRow key={v.id} data-testid={`policy-trends-row-${v.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {(v.createdAt || '').slice(0, 10)}
                        </TableCell>
                        <TableCell>
                          <Badge className={SEVERITY_STYLES[v.severity] || SEVERITY_STYLES.Minor}>
                            {v.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs capitalize">{v.confidence}</TableCell>
                        <TableCell className="text-xs capitalize">
                          {v.recordKind === 'audit' ? labels.audit : labels.incident}
                        </TableCell>
                        <TableCell className="text-sm font-medium max-w-[200px] truncate" title={v.source}>
                          {v.source}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700 max-w-[320px] truncate" title={v.description}>
                          {v.description}
                        </TableCell>
                        <TableCell>
                          {v.dismissed ? (
                            <Badge variant="outline">{labels.dismissed}</Badge>
                          ) : (
                            <Badge className="bg-red-600 text-white">{labels.open}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPolicyViolations;
